import React from 'react';
import { Link } from 'react-router-dom';
import StoreNavigation from '../components/StoreNavigation';
import StoreFooter from '../components/StoreFooter';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';
import { Leaf, RefreshCcw, Shield, Wrench, CheckCircle, ArrowRight } from 'lucide-react';

const Sustainability = () => {
  const { getCartItemCount } = useCart();

  const pillars = [
    {
      icon: RefreshCcw,
      title: 'Wash & Re-Wear',
      description: 'Our suits are designed for repeated use, not disposable after a few wears. Machine washable construction means longer life.'
    },
    {
      icon: Shield,
      title: 'Premium Materials',
      description: 'Premium weight fabric with reinforced stress points. Quality that lasts means less waste in landfills.'
    },
    {
      icon: Wrench,
      title: 'Built to Last',
      description: 'Durable construction that stands up to daily use. We\'re working on repair solutions to help extend your suit\'s life even further.'
    },
    {
      icon: Leaf,
      title: 'Thoughtful Design',
      description: 'Every feature is intentional - designed to reduce waste through longevity rather than disposability.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <StoreNavigation cartItemCount={getCartItemCount()} />

      {/* Hero */}
      <section className="pt-32 pb-24 bg-gradient-to-br from-[#004D51] to-[#003338] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#F39C49] rounded-full flex items-center justify-center mx-auto mb-8">
              <Leaf className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Built to <span className="text-[#F39C49]">Last</span>
            </h1>
            <p className="text-xl text-[#CFD2D4] max-w-2xl mx-auto">
              Sustainability isn't about perfection—it's about making better choices. 
              Here's how we're doing our part.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-black text-[#004D51] mb-6">
              Our Approach to Sustainability
            </h2>
            <p className="text-xl text-[#545759]">
              The workwear industry is notoriously wasteful. Cheap disposable suits end up in landfills 
              after minimal use. We're taking a different approach.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={index}
                className="bg-white border-4 border-[#004D51] rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 bg-[#DB5A14] rounded-lg flex items-center justify-center mb-6">
                  <pillar.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-black text-[#004D51] mb-4">{pillar.title}</h3>
                <p className="text-[#545759] text-lg">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 bg-[#F3CEB2]/20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-black text-[#004D51] mb-6">
                  Material <span className="text-[#DB5A14]">Matters</span>
                </h2>
                <div className="space-y-4 text-[#545759] text-lg">
                  <p>
                    We use premium weight polyester with carbon fiber threading—chosen for durability, 
                    static dissipation, and wash-ability. This isn't cheap fabric that falls apart.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                      <span>Heavy-duty construction withstands workshop conditions</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                      <span>Machine washable for repeated use</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                      <span>Reinforced stress points prevent premature wear</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-6 h-6 text-[#DB5A14] mr-3 flex-shrink-0 mt-0.5" />
                      <span>Patent pending design built specifically for women</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="relative">
                <div className="absolute -top-4 -right-4 w-full h-full bg-[#004D51] rounded-2xl transform -rotate-3"></div>
                <img
                  src="https://cdn.shopify.com/s/files/1/0770/5572/1703/files/160126_VestaWorkwear-09.jpg?v=1772281309"
                  alt="Premium materials"
                  className="relative rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Built to Last Section - replacing Repair Program */}
      <section className="py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 mx-auto mb-6 text-[#DB5A14]" />
            <h2 className="text-4xl font-black text-[#004D51] mb-6">
              Built to Last
            </h2>
            <p className="text-xl text-[#545759] mb-8 max-w-2xl mx-auto">
              Our suits are designed to withstand the demands of daily workshop use. 
              We're also working on repair solutions to help you extend your suit's life 
              even further—stay tuned for updates.
            </p>
            <Link to="/shop">
              <Button className="bg-[#004D51] hover:bg-[#003338] text-white font-black px-8 py-4 text-lg">
                Shop Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Honesty Section */}
      <section className="py-24 bg-[#004D51] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-black mb-6">
              We're Not Perfect. <span className="text-[#F39C49]">Yet.</span>
            </h2>
            <p className="text-xl text-[#CFD2D4] mb-8">
              We're a small brand doing what we can. We're not claiming to save the planet. 
              But we believe in making gear that lasts, reducing waste where we can, and 
              being honest about our journey.
            </p>
            <p className="text-lg text-[#CFD2D4]">
              As we grow, we're committed to finding more ways to reduce our environmental 
              impact—from packaging to shipping to end-of-life programs.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#DB5A14] to-[#F39C49] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-black mb-6">
              Invest in Quality That Lasts
            </h2>
            <p className="text-xl text-white/90 mb-8">
              A suit that lasts is better than five that don't. Shop the difference.
            </p>
            <Link to="/shop">
              <Button className="bg-white text-[#DB5A14] hover:bg-[#F3CEB2] font-black px-8 py-6 text-lg">
                Shop Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
};

export default Sustainability;
